import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOSchema from '@/components/base/SEOSchema';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'european-american');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="WebPage"
        title={`${currentCategory.name} - 精选gay片资源导航`}
        description={currentCategory.description}
        url={`/category/${activeSlug}`}
      />

      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <section className="py-12 md:py-16 bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {currentCategory.name}
                <span className="text-accent-500"> · gay片分类</span>
              </h1>
              <p className="text-foreground-500 leading-relaxed">
                {currentCategory.description}
              </p>
              <p className="text-sm text-foreground-400 mt-2">
                共 {categoryArticles.length} 篇相关gay片文章
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-accent-500 text-background-950'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-500/20 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="px-2.5 py-1 bg-accent-500/90 text-background-950 text-xs font-medium rounded-md">
                            精选
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-accent-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-3">
                          <span>{article.publishDate}</span>
                          <span>{article.readTime}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                          <span>{article.rating}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无gay片文章，请浏览其他分类。</p>
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-8">
              探索更多gay片分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories
                .filter((c) => c.slug !== activeSlug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-accent-500/20 transition-all"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500/10 flex-shrink-0">
                      <i className={`${cat.icon} text-accent-500`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{cat.name}</p>
                      <p className="text-xs text-foreground-400">{cat.articleCount}篇</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}